/**
 * Stepped auto-scroll for the category carousel.
 *
 * The theme's own slideshow autoplay is left off for this row: in `infinite` mode its
 * slide index and the scroller's position drift apart, so it advances `current` on a
 * timer while `scrollLeft` never moves. This walks the scroller directly instead, one
 * card at a time, which also keeps CSS scroll-snap working.
 *
 * Pauses on hover, on keyboard focus, and while the tab is hidden. Deliberately not gated
 * on an IntersectionObserver: the theme's own [in-viewport] observer is what leaves these
 * carousels frozen, and a timer that only ticks a scroll position costs nothing off screen.
 * Does nothing when the visitor prefers reduced motion.
 */

const STEP_INTERVAL_FALLBACK = 3000;
const RESUME_DELAY_AFTER_INTERACTION = 6000;
const GLIDE_DURATION = 550;

/** easeInOutQuad — gentle start and stop, no overshoot. */
const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

class CategoryAutoscroll {
  /** @type {HTMLElement} */
  #scroller;
  /** @type {number} */
  #intervalMs;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resumeTimer;
  /** @type {number | undefined} */
  #frame;
  #hovered = false;
  #focused = false;

  /**
   * @param {HTMLElement} root - The element carrying data-category-autoscroll.
   * @param {HTMLElement} scroller - The horizontally scrollable element.
   */
  constructor(root, scroller) {
    this.#scroller = scroller;
    this.#intervalMs = Number(root.dataset.categoryAutoscroll) || STEP_INTERVAL_FALLBACK;

    root.addEventListener('pointerenter', this.#onPointerEnter);
    root.addEventListener('pointerleave', this.#onPointerLeave);
    root.addEventListener('focusin', this.#onFocusIn);
    root.addEventListener('focusout', this.#onFocusOut);
    scroller.addEventListener('pointerdown', this.#deferResume);
    scroller.addEventListener('wheel', this.#deferResume, { passive: true });
    document.addEventListener('visibilitychange', this.#sync);

    this.#sync();
  }

  get #canRun() {
    return !this.#hovered && !this.#focused && !document.hidden;
  }

  #onPointerEnter = () => {
    this.#hovered = true;
    this.#sync();
  };

  #onPointerLeave = () => {
    this.#hovered = false;
    this.#sync();
  };

  #onFocusIn = () => {
    this.#focused = true;
    this.#sync();
  };

  #onFocusOut = () => {
    this.#focused = false;
    this.#sync();
  };

  /** Holds off for a beat after the visitor scrolls or drags, so we don't fight them. */
  #deferResume = () => {
    this.#stop();
    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = setTimeout(this.#sync, RESUME_DELAY_AFTER_INTERACTION);
  };

  #sync = () => {
    if (this.#canRun) this.#start();
    else this.#stop();
  };

  #start() {
    if (this.#timer) return;
    this.#timer = setInterval(this.#step, this.#intervalMs);
  }

  #stop() {
    clearInterval(this.#timer);
    this.#timer = undefined;
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }

  /**
   * Animates scrollLeft by hand.
   *
   * Native smooth scrolling is unreliable on this element: with `scroll-behavior: smooth`
   * in play, a scrollTo call is cancelled before it emits a single scroll event. Assigning
   * scrollLeft under `scroll-behavior: auto` always lands, so the easing happens here.
   *
   * @param {number} to - Target scrollLeft.
   */
  #glideTo(to) {
    const scroller = this.#scroller;
    const from = scroller.scrollLeft;
    const distance = to - from;
    if (!distance) return;

    if (this.#frame) cancelAnimationFrame(this.#frame);
    const started = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - started) / GLIDE_DURATION, 1);
      scroller.scrollLeft = from + distance * ease(progress);

      if (progress < 1) this.#frame = requestAnimationFrame(tick);
      else this.#frame = undefined;
    };

    this.#frame = requestAnimationFrame(tick);
  }

  #step = () => {
    const scroller = this.#scroller;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;

    // Nothing to scroll through — every card already fits.
    if (maxScroll <= 1) return;

    const slide = scroller.firstElementChild;
    if (!(slide instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
    const step = slide.getBoundingClientRect().width + gap;

    // `- 1` absorbs sub-pixel rounding so the last step still registers as the end.
    const atEnd = scroller.scrollLeft >= maxScroll - 1;

    this.#glideTo(atEnd ? 0 : Math.min(scroller.scrollLeft + step, maxScroll));
  };
}

function init() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  for (const root of document.querySelectorAll('[data-category-autoscroll]')) {
    if (!(root instanceof HTMLElement) || root.dataset.categoryAutoscrollReady) continue;

    const scroller = root.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) continue;

    root.dataset.categoryAutoscrollReady = 'true';
    new CategoryAutoscroll(root, scroller);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Re-run after the theme editor re-renders a section.
document.addEventListener('shopify:section:load', init);
